import { Suspense } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import Preloader from "@krea/customer-features/shared-components/preloader";
import { useGetFieldRequests } from "@krea/customer-features/store/field-requests/hooks/queries";
import { IFieldRequest } from "@krea/customer-features/types/fieldRequest";
import { LoanApplicationStates } from "@krea/customer-features/types/loanApplication";
import { getFilteredFieldRequests } from "@krea/customer-features/utils";

import { PrivateRoute } from "@/krea-direct/private-route";
import { NotFound } from "@/pages/not-found";

import { useGetCurrentLoanApplication } from "store/current-loan-application/hooks/queries";

import { ApplicationQuestionsPreview } from "./preview";
import { ApplicationQuestionsStep } from "./step";

interface IApplicationQuestionsRootRedirectProps {
  state: LoanApplicationStates;
  fieldRequests: IFieldRequest[];
}

const ApplicationQuestionsRootRedirect = ({
  state,
  fieldRequests,
}: IApplicationQuestionsRootRedirectProps) => {
  switch (state) {
    case LoanApplicationStates.DRAFT:
      return <Navigate replace={true} to={fieldRequests[0].field} />;

    default:
      return <Navigate replace={true} to="preview" />;
  }
};

export const ApplicationQuestions = () => {
  const { data } = useGetCurrentLoanApplication();
  const { state, triageFields } = data ?? {};
  const { data: fieldRequests = [], isLoading } = useGetFieldRequests();

  const filteredFieldRequests = getFilteredFieldRequests(
    triageFields,
    fieldRequests,
  );

  if (!filteredFieldRequests[0] || isLoading || !state) {
    return (
      <Preloader
        className="tw-min-h-[300px] tw-w-full tw-bg-white tw-text-primary"
        size="xl"
      />
    );
  }

  return (
    <Suspense fallback={<></>}>
      <Routes>
        <Route
          path="/"
          element={
            <PrivateRoute
              restrictedBasedOnState
              redirectOnPermissionDenied="preview"
            >
              <Outlet />
            </PrivateRoute>
          }
        >
          <Route
            index
            element={
              <ApplicationQuestionsRootRedirect
                state={state}
                fieldRequests={filteredFieldRequests}
              />
            }
          />

          {filteredFieldRequests.map((step) => (
            <Route
              path={step.field}
              key={`route-${step.field}`}
              element={
                <ApplicationQuestionsStep
                  step={step}
                  steps={filteredFieldRequests}
                />
              }
            />
          ))}

          <Route path="preview" element={<ApplicationQuestionsPreview />} />

          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
};
